import { HouseholdSegment } from './householdSegment';
import {
  filterRedundantRestrictions,
  filterUserOnlyRestrictions,
  sortDietaryRestrictionsByRanking,
  canonicalRestrictionLabel,
} from './dietaryRestrictions';

/**
 * Shared badge pipeline for recipe cards and detail views.
 *
 * Every surface that renders a recipe's dietary-restriction/tag badge row
 * should build it through getRecipeBadges (or, for split layouts, the
 * getPrioritizedRestrictions / filterTagsForViewer pieces) so filtering and
 * ordering stay consistent app-wide:
 *   1. Redundant restrictions are dropped (Vegan implies Vegetarian, ...).
 *   2. Viewer-only restrictions (Kosher, Halal, Low-FODMAP, Low-Residue)
 *      are dropped unless the viewer's profile has them.
 *   3. Restrictions matching the viewer's profile come first; both groups are
 *      sorted by the display-importance ranking.
 *   4. Tags that are meaningless to the viewer's household are dropped
 *      (see FAMILY_ONLY_TAGS); the rest keep the recipe's own order.
 */

export type RecipeBadge = { text: string; type: 'dietary' | 'tag' };

export type RecipeBadgeViewer = {
  /** The viewer's own dietary restrictions. Omit/empty when unknown. */
  dietaryRestrictions?: string[];
  /**
   * The viewer's household framing (householdSegment util). Omit when
   * unknown — unknown households still see family-only tags, matching
   * householdSegment's own default of 'family'.
   */
  householdSegment?: HouseholdSegment;
};

export type RecipeBadgeOptions = {
  /**
   * Re-add the viewer's own restrictions even when the redundancy filter
   * dropped them. Used by the marketing demo, where the point is showing
   * the viewer their selections on the card.
   */
  includeUserSelectedRedundant?: boolean;
};

/**
 * Tags only relevant to a particular household segment (the tag-side
 * analogue of USER_ONLY_RESTRICTIONS): "kid-friendly" is noise for
 * households without kids. Compared case-insensitively.
 */
const FAMILY_ONLY_TAGS = ['kid-friendly'];

/**
 * Drops tags that carry no signal for the viewer's household. An unknown
 * segment (no profile yet, logged-out viewer) keeps every tag.
 */
export const filterTagsForViewer = (
  tags: string[],
  segment?: HouseholdSegment
): string[] => {
  if ((segment ?? 'family') === 'family') {
    return tags;
  }
  return tags.filter((t) => !FAMILY_ONLY_TAGS.includes(t.toLowerCase()));
};

/**
 * Full restriction pipeline: redundancy filter, viewer-only filter, then
 * viewer-matching restrictions first with each group ranked.
 */
export const getPrioritizedRestrictions = (
  recipeRestrictions: string[],
  viewer: RecipeBadgeViewer = {},
  options: RecipeBadgeOptions = {}
): string[] => {
  const viewerRestrictions = viewer.dietaryRestrictions ?? [];

  const filtered = filterUserOnlyRestrictions(
    filterRedundantRestrictions(recipeRestrictions),
    viewerRestrictions
  );

  // Case-insensitive throughout: a recipe stored as "Low-Fodmap" must still
  // match a viewer whose profile says "Low-FODMAP" (see the note in
  // utils/dietaryRestrictions on historical casing drift).
  const viewerHas = (restriction: string): boolean =>
    viewerRestrictions.some((v) => v.toLowerCase() === restriction.toLowerCase());

  const userSelected = options.includeUserSelectedRedundant
    ? viewerRestrictions.filter(
        (r) =>
          recipeRestrictions.some((rr) => rr.toLowerCase() === r.toLowerCase()) &&
          !filtered.some((f) => f.toLowerCase() === r.toLowerCase())
      )
    : [];
  const all = [...userSelected, ...filtered];

  const userMatching = all.filter(viewerHas);
  const others = all.filter((r) => !viewerHas(r));

  return [
    ...sortDietaryRestrictionsByRanking(userMatching),
    ...sortDietaryRestrictionsByRanking(others),
  ].map(canonicalRestrictionLabel);
};

/**
 * Users on these diets see only meatless recipes anyway, so the badge would be
 * noise on every card.
 */
const MEATLESS_DIETS = ['vegetarian', 'vegan'];

/**
 * The single meatless badge for the swipe/browse surfaces that show one rather
 * than the full getRecipeBadges row: the wizard picker's cards and the Taste
 * Tuner's. For everyone not already on a meatless diet — pescatarians included,
 * whose shortlists mix fish dishes with meatless ones — it's the heads-up that
 * a tasty-looking card has no animal protein in it.
 *
 * Vegan wins over Vegetarian when a recipe is tagged with both. That's the same
 * call filterRedundantRestrictions makes for the full badge row, kept in step
 * here so the two surfaces don't drift apart.
 *
 * Returns null when there's no badge to show, either because the recipe isn't
 * meatless or because the viewer's own diet makes saying so redundant.
 */
export const getMeatlessLabel = (
  recipe: { dietaryRestrictions?: string[] },
  viewer: RecipeBadgeViewer = {}
): string | null => {
  const viewerIsMeatless = (viewer.dietaryRestrictions ?? []).some((r) =>
    MEATLESS_DIETS.includes(r.toLowerCase())
  );
  if (viewerIsMeatless) return null;

  const diets = new Set(
    (recipe.dietaryRestrictions ?? []).map((d) => d.toLowerCase())
  );
  if (diets.has('vegan')) return 'Vegan';
  if (diets.has('vegetarian')) return 'Vegetarian';
  return null;
};

/**
 * Builds the combined badge row for a recipe: prioritized dietary
 * restrictions followed by viewer-relevant tags. Callers slice to their
 * layout's badge budget with splitBadgesForDisplay and render the overflow
 * with the MoreRestrictionsMenu component.
 */
export const getRecipeBadges = (
  recipe: { dietaryRestrictions?: string[]; tags?: string[] },
  viewer: RecipeBadgeViewer = {},
  options: RecipeBadgeOptions = {}
): RecipeBadge[] => {
  const restrictions = getPrioritizedRestrictions(
    recipe.dietaryRestrictions ?? [],
    viewer,
    options
  );
  const tags = filterTagsForViewer(recipe.tags ?? [], viewer.householdSegment);

  return [
    ...restrictions.map((text) => ({ text, type: 'dietary' as const })),
    ...tags.map((text) => ({ text, type: 'tag' as const })),
  ];
};

export type BadgeDisplaySplit = {
  /** Badges that fit the layout's budget, in getRecipeBadges order. */
  visible: RecipeBadge[];
  /**
   * Dietary restrictions that did not fit. Overflowing tags are dropped
   * silently: the "+N more" affordance counts restrictions only, since those
   * are what a viewer scanning a card actually needs the full list of.
   */
  hiddenRestrictions: string[];
};

/**
 * Splits a badge row into what a card renders and the dietary restrictions
 * its "+N more" affordance should reveal. Restrictions sort ahead of tags in
 * getRecipeBadges, so anything hidden here is genuinely past the budget.
 */
export const splitBadgesForDisplay = (
  badges: RecipeBadge[],
  maxVisible: number
): BadgeDisplaySplit => ({
  visible: badges.slice(0, maxVisible),
  hiddenRestrictions: badges
    .slice(maxVisible)
    .filter((b) => b.type === 'dietary')
    .map((b) => b.text),
});
